import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Activate() {
  const {
    t
  } = useI18n();

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/activate`, { token, password });
      setDone(true);
      toast.success("Account activated — you can now sign in");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "This invitation link is invalid or has expired");
    } finally {
      setBusy(false);
    }
  };

  return (
    (<div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="activate-page">
      <Card className="w-full max-w-md p-8">
        <div className="mb-5 flex items-center gap-2">
          <KeyRound size={20} className="text-primary" />
          <h1 className="text-lg font-semibold">{t("Activate your account")}</h1>
        </div>
        {!token ? (
          <p className="text-sm text-muted-foreground" data-testid="activate-no-token">{t(
            "This activation link is missing its token. Please use the exact link your administrator shared."
          )}</p>
        ) : done ? (
          <div className="flex items-center gap-2 text-status-good" data-testid="activate-done">
            <CheckCircle2 size={18} />{" "}{t("Account activated. Redirecting to sign in…")}</div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("Set your own password to finish joining your team.")}</p>
            <div><Label>{t("New password")}</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" placeholder={t("min 8 characters")} data-testid="activate-password" /></div>
            <div><Label>{t("Confirm password")}</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" data-testid="activate-confirm" /></div>
            <Button className="w-full" onClick={submit} disabled={busy} data-testid="activate-submit">{busy ? t("Activating…") : t("Activate account")}</Button>
            <p className="text-center text-xs text-muted-foreground"><Link to="/login" className="underline">{t("Back to sign in")}</Link></p>
          </div>
        )}
      </Card>
    </div>)
  );
}
